import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DynamicIcon } from '../components/icons';

// ── Guide data ────────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    id: 'intro', icon: 'BookOpen', label: 'Introduction',
    content: [
      { type: 'p', text: 'The AIDAS Platform (APFv2) is an enterprise AI/ML and analytics operating system. It enables organizations to:' },
      { type: 'bullets', items: ['Build and run AI/ML and data workflows using a visual Flow Editor', 'Create and share interactive dashboards and business applications', 'Query data using natural language (AI Insight)', 'Manage multi-tenant deployments across partners and customers', 'Connect to external databases and automate pipelines with a Scheduler', 'Monitor activity through audit logs, alert rules, and support tickets'] },
      { type: 'table', headers: ['User Group', 'Description'], rows: [['Platform Admins', 'Full access to all system features, tenants, partners, plans'], ['Partner Admins', 'Manage their assigned tenants and users'], ['Tenant Users', 'Access assigned apps, dashboards, flows, and AI tools']] },
    ]
  },
  {
    id: 'start', icon: 'LogIn', label: 'Getting Started',
    content: [
      { type: 'h3', text: 'Logging In' },
      { type: 'steps', items: ['Navigate to your platform URL in a browser.', 'Enter your email and password on the login screen.', 'Click Sign In.'] },
      { type: 'callout', variant: 'important', text: 'If your password has expired, you will be redirected to the Password Change screen immediately. Platform access is blocked until the password is updated.' },
      { type: 'h3', text: 'Resetting a Password' },
      { type: 'bullets', items: ['Click Forgot Password on the login page.', 'Enter your registered email address.', 'Follow the link sent to your inbox to set a new password.'] },
    ]
  },
  {
    id: 'nav', icon: 'LayoutDashboard', label: 'Navigation',
    content: [
      { type: 'h3', text: 'Sidebar' },
      { type: 'table', headers: ['Section', 'Description'], rows: [['Home', 'Landing dashboard and Execution Status'], ['AIDAS Apps', 'System-assigned business app bundles (tenant view)'], ['Published Apps', 'Organization-wide shared dashboards and flows'], ['My Apps', 'Your personal workspace apps'], ['Creator Studio', 'Flows, AI Insight, Dashboards, Business Apps'], ['Data', 'Data Sources, Data Models, Knowledge Base, Scheduler, Alert Rules'], ['Administration', 'Partners, Tenants, Users, Roles, Plans, Credits, Settings']] },
      { type: 'h3', text: 'Top Bar' },
      { type: 'bullets', items: ['Bell icon — opens the Notification Center (alert triggers, system events)', 'User avatar — opens profile menu (My Profile, Appearance, Sign Out)'] },
    ]
  },
  {
    id: 'flows', icon: 'Workflow', label: 'Flows (Workflow Studio)',
    content: [
      { type: 'p', text: 'Flows is the visual drag-and-drop engine for building AI, ML, ETL, and automation pipelines. Path: /flows' },
      { type: 'h3', text: 'Flow Editor Canvas' },
      { type: 'table', headers: ['Area', 'Purpose'], rows: [['Node Panel (left)', 'Library of available nodes'], ['Canvas (center)', 'Drag, drop, and connect nodes'], ['Config Panel (right)', 'Configure the selected node']] },
      { type: 'h3', text: 'Node Categories' },
      { type: 'bullets', items: ['Data Nodes — File Source, File Object, Database Reader, API Connector', 'Transform Nodes — Filter, Join, Aggregate, Formula', 'AI/ML Nodes — LLM Prompt, AI Insight, AI Agent, ML Model', 'Output Nodes — Dashboard Writer, Email, Webhook, Database Writer'] },
      { type: 'h3', text: 'Running a Flow' },
      { type: 'steps', items: ['Click ▶ Run in the top toolbar.', 'Monitor progress in the Execution Log panel (bottom or via /logs).', 'On completion, output data is available to downstream nodes or published dashboards.'] },
      { type: 'h3', text: 'Publishing a Flow' },
      { type: 'steps', items: ['Click Publish in the toolbar.', 'Choose a Content Type (AI/ML Prediction, GenAI App, Workflow App, ETL).', 'Set the sharing scope (private, organization-wide, or specific tenants).', 'Confirm to make the flow available in the Business Apps catalogue.'] },
    ]
  },
  {
    id: 'ai', icon: 'BrainCircuit', label: 'AI Insight',
    content: [
      { type: 'p', text: 'AI Insight is a natural language query (NLQ) interface. Ask questions in plain English and get instant data answers, charts, and summaries. Path: /ai-insight' },
      { type: 'h3', text: 'Starting a Conversation' },
      { type: 'steps', items: ['Navigate to AI Insight in the sidebar.', 'Select a Data Source or Knowledge Base to query against.', 'Type your question in the chat box and press Enter.'] },
      { type: 'h3', text: 'Result Types' },
      { type: 'bullets', items: ['Text answers — prose summaries generated by the AI model', 'Data tables — tabular result sets from SQL generated automatically', 'Charts — auto-rendered bar, line, or pie charts'] },
      { type: 'callout', variant: 'tip', text: 'Conversations are stateful. You can ask follow-up questions that refine the previous query (e.g., "Now show only Q3 data").' },
    ]
  },
  {
    id: 'dashboards', icon: 'BarChart2', label: 'Dashboards',
    content: [
      { type: 'p', text: 'The Dashboard Studio lets you create and share interactive analytics dashboards. Path: /dashboards' },
      { type: 'h3', text: 'Widget Types' },
      { type: 'table', headers: ['Widget', 'Description'], rows: [['Bar / Line / Pie Chart', 'Visualization bound to a data model query'], ['KPI Card', 'Single metric with optional trend'], ['Table', 'Paginated grid of rows'], ['Text / Markdown', 'Freeform annotations'], ['Filter Bar', 'User-controlled filter applied to all widgets']] },
      { type: 'h3', text: 'Sharing' },
      { type: 'bullets', items: ['Private — only you can view', 'Organization-Wide — all users in your tenant', 'Embed — generate an embed URL for external portals'] },
    ]
  },
  {
    id: 'apps', icon: 'LayoutGrid', label: 'Business Apps',
    content: [
      { type: 'p', text: 'Business Apps is the marketplace where published content is bundled into application packages and assigned to tenants. Path: /business-apps' },
      { type: 'h3', text: 'Content Types' },
      { type: 'table', headers: ['Type', 'Description'], rows: [['AI/ML Predictions', 'ML model inference runner'], ['GenAI App', 'LLM/GenAI powered application'], ['Dashboard', 'Interactive analytics dashboard'], ['Workflow App', 'Automated business process']] },
      { type: 'h3', text: 'Creating an App Bundle' },
      { type: 'steps', items: ['Click + New App Bundle.', 'Give it a Name and optional Icon.', 'Add content items (dashboards, flows, ML models) from the catalogue.', 'Click Save.'] },
    ]
  },
  {
    id: 'data', icon: 'Database', label: 'Data Sources & Models',
    content: [
      { type: 'h3', text: 'Data Sources (/datasources)' },
      { type: 'steps', items: ['Click + New Connection.', 'Select a connector type: PostgreSQL, MySQL, MSSQL, REST API, S3, or others.', 'Fill in host, port, database name, credentials.', 'Click Test Connection to verify.', 'Click Save.'] },
      { type: 'callout', variant: 'tip', text: 'For on-premise databases behind a firewall, use a Relay Agent. The connection is routed securely through the agent.' },
      { type: 'h3', text: 'Data Models (/datamodels)' },
      { type: 'p', text: 'Data Models define the semantic layer — the business-friendly representation of raw database tables, joins, and calculated fields.' },
      { type: 'steps', items: ['Click + New Data Model.', 'Select a Data Source.', 'Add tables and define joins using the visual canvas.', 'Add calculated fields and filters.', 'Click Publish to make the model available to dashboards and AI Insight.'] },
    ]
  },
  {
    id: 'scheduler', icon: 'Clock', label: 'Scheduler & Alerts',
    content: [
      { type: 'h3', text: 'Scheduler (/automations)' },
      { type: 'steps', items: ['Click + New Schedule.', 'Select the Flow to automate.', 'Set the trigger type: Cron, Interval, or One-time.', 'Set optional timeout and retry settings.', 'Click Save & Activate.'] },
      { type: 'table', headers: ['Action', 'Description'], rows: [['Pause', 'Temporarily disable without deleting'], ['Resume', 'Re-activate a paused schedule'], ['Run Now', 'Trigger an immediate manual run'], ['Delete', 'Permanently remove the schedule']] },
      { type: 'h3', text: 'Alert Rules (/alerts)' },
      { type: 'steps', items: ['Click + New Alert Rule.', 'Select a Data Source and metric.', 'Define a condition (e.g., value > 1000).', 'Set notification channels: Email, Webhook, or in-app.', 'Set evaluation frequency.', 'Click Save & Enable.'] },
    ]
  },
  {
    id: 'admin', icon: 'ShieldCheck', label: 'Administration',
    content: [
      { type: 'h3', text: 'Users & Roles' },
      { type: 'bullets', items: ['Add User: Click + Invite User, enter email, select role.', 'Edit User: Click a user row to change name, role, or status.', 'Deactivate User: Toggle the Active switch to revoke access without deleting.', 'Reset Password: Click the action menu → Send Password Reset.'] },
      { type: 'h3', text: 'Built-in Roles' },
      { type: 'table', headers: ['Role', 'Name', 'Scope'], rows: [['r1', 'Platform Super Admin', 'Full platform'], ['r2', 'Platform Admin', 'Full platform'], ['r3', 'Partner Admin', 'Partner + assigned tenants'], ['r4', 'Tenant Admin', 'Single tenant'], ['r6', 'Tenant Creator', 'Create content within tenant'], ['r7', 'Tenant Viewer', 'Read-only access to apps']] },
      { type: 'h3', text: 'Tenant Management' },
      { type: 'steps', items: ['Click + New Tenant.', 'Fill in Organization Name, Domain, and Plan.', 'Assign to a Partner (optional).', 'Click Create.'] },
    ]
  },
  {
    id: 'relay', icon: 'Server', label: 'Relay Agents',
    content: [
      { type: 'p', text: 'Relay Agents are lightweight software agents installed on-premises that allow the AIDAS platform to securely reach databases inside private networks without opening firewall ports.' },
      { type: 'callout', variant: 'note', text: 'The agent initiates an outbound WebSocket connection to the platform. All data flows through this secure tunnel: AIDAS Platform ──── HTTPS ────► Relay Agent ──── Internal Network ────► Database' },
      { type: 'h3', text: 'Installing a Relay Agent' },
      { type: 'steps', items: ['Go to Settings → Relay Agents.', 'Click + New Agent.', 'Give the agent a Name and optional Description.', 'Click Generate Token — copy the registration token.', 'On your on-premise server run: pip install aidas-relay-agent && aidas-relay-agent register --token <TOKEN> --server https://your-platform-url && aidas-relay-agent start'] },
      { type: 'table', headers: ['Status', 'Meaning'], rows: [['ONLINE', 'Agent is connected and healthy'], ['IDLE', 'Agent missed heartbeat (>2 min); will reconnect'], ['PENDING', 'Token issued, agent not yet connected'], ['OFFLINE', 'Agent manually deregistered']] },
    ]
  },
  {
    id: 'shortcuts', icon: 'Keyboard', label: 'Shortcuts & Glossary',
    content: [
      { type: 'h3', text: 'Keyboard Shortcuts' },
      { type: 'table', headers: ['Shortcut', 'Action'], rows: [['Ctrl + Enter', 'Run SQL / Flow'], ['Ctrl + S', 'Save current work'], ['Escape', 'Close modal / panel']] },
      { type: 'h3', text: 'Glossary' },
      { type: 'table', headers: ['Term', 'Definition'], rows: [['Flow', 'A visual pipeline of connected processing nodes'], ['Node', 'A single processing step within a flow'], ['Data Model', 'A semantic layer mapping raw tables to business concepts'], ['App Bundle', 'A packaged collection of dashboards and flows'], ['Tenant', 'An organization with its own isolated workspace'], ['Partner', 'A reseller or implementor managing multiple tenants'], ['Relay Agent', 'On-premise software bridging private networks to the platform'], ['NLQ', 'Natural Language Query — asking questions in plain English'], ['Credit', 'A unit of compute resource consumption']] },
    ]
  },
  {
    id: 'nodes', icon: 'Box', label: 'Node Reference Guide', iframe: '/node_user_guide.html',
    content: []
  },
];

// ── Block renderers ───────────────────────────────────────────────────────────
const CALLOUT_STYLES = {
  tip:       { bg: 'bg-green-50 dark:bg-green-900/20',  border: 'border-green-300 dark:border-green-700',  icon: 'Lightbulb',   text: 'text-green-800 dark:text-green-300',  label: 'Tip' },
  important: { bg: 'bg-amber-50 dark:bg-amber-900/20',  border: 'border-amber-300 dark:border-amber-700',  icon: 'AlertTriangle', text: 'text-amber-800 dark:text-amber-300', label: 'Important' },
  note:      { bg: 'bg-blue-50 dark:bg-blue-900/20',    border: 'border-blue-300 dark:border-blue-700',    icon: 'Info',        text: 'text-blue-800 dark:text-blue-300',    label: 'Note' },
  caution:   { bg: 'bg-red-50 dark:bg-red-900/20',      border: 'border-red-300 dark:border-red-700',      icon: 'ShieldAlert', text: 'text-red-800 dark:text-red-300',      label: 'Caution' },
};

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{block.text}</p>;
    case 'h3':
      return <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mt-5 mb-2 flex items-center gap-2"><span className="w-1 h-4 bg-indigo-500 rounded-full inline-block" />{block.text}</h3>;
    case 'bullets':
      return (
        <ul className="space-y-1.5 ml-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-2 ml-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
              <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              {item}
            </li>
          ))}
        </ol>
      );
    case 'table':
      return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 my-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                {block.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {block.rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-2.5 text-gray-600 dark:text-gray-300 ${ci === 0 ? 'font-medium text-gray-800 dark:text-gray-200' : ''}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'callout': {
      const s = CALLOUT_STYLES[block.variant] || CALLOUT_STYLES.note;
      return (
        <div className={`flex gap-3 p-4 rounded-lg border ${s.bg} ${s.border} my-2`}>
          <DynamicIcon name={s.icon} className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} />
          <div className={`text-sm ${s.text}`}>
            <span className="font-semibold">{s.label}: </span>{block.text}
          </div>
        </div>
      );
    }
    default: return null;
  }
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const UserGuidePage = ({ onClose }) => {
  const [activeId, setActiveId] = useState('intro');
  const [search, setSearch]     = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const active = SECTIONS.find(s => s.id === activeId) || SECTIONS[0];

  const filtered = search.trim()
    ? SECTIONS.filter(s => s.label.toLowerCase().includes(search.toLowerCase()))
    : SECTIONS;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50 dark:bg-gray-950">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
          >
            <DynamicIcon name="PanelLeft" className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-600 rounded-lg">
              <DynamicIcon name="BookOpen" className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900 dark:text-white text-sm">AIDAS Platform — User Guide</span>
            <span className="text-xs px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full font-medium">v2.0</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          title="Close"
        >
          <DynamicIcon name="X" className="w-5 h-5" />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">

        {/* Sidebar */}
        <AnimatePresence initial={false}>
          {sidebarOpen && (
            <motion.aside
              key="sidebar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 260, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              className="shrink-0 overflow-hidden bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col"
            >
              {/* Search */}
              <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                <div className="relative">
                  <DynamicIcon name="Search" className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search sections…"
                    className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                </div>
              </div>

              {/* Nav items */}
              <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
                {filtered.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => { setActiveId(s.id); setSearch(''); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                      activeId === s.id
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <DynamicIcon name={s.icon} className="w-4 h-4 shrink-0" />
                    <span className="truncate">{s.label}</span>
                  </button>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-3 border-t border-gray-100 dark:border-gray-800 text-center text-xs text-gray-400">
                © AIDAS Platform — Confidential
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Content */}
        <main className="flex-1 overflow-hidden flex flex-col">
          {/* Hero banner — hidden for iframe sections */}
          {!active.iframe && (
          <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 px-8 py-8 overflow-hidden shrink-0">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
            <div className="relative z-10 flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                <DynamicIcon name={active.icon} className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-indigo-200 text-xs uppercase tracking-widest font-medium mb-0.5">
                  {SECTIONS.findIndex(s => s.id === activeId) + 1} of {SECTIONS.length}
                </p>
                <h1 className="text-2xl font-bold text-white">{active.label}</h1>
              </div>
            </div>

            {/* Prev / Next */}
            <div className="absolute right-6 top-1/2 -translate-y-1/2 flex gap-2">
              {SECTIONS.findIndex(s => s.id === activeId) > 0 && (
                <button
                  onClick={() => setActiveId(SECTIONS[SECTIONS.findIndex(s => s.id === activeId) - 1].id)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <DynamicIcon name="ChevronUp" className="w-4 h-4" />
                </button>
              )}
              {SECTIONS.findIndex(s => s.id === activeId) < SECTIONS.length - 1 && (
                <button
                  onClick={() => setActiveId(SECTIONS[SECTIONS.findIndex(s => s.id === activeId) + 1].id)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  <DynamicIcon name="ChevronDown" className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          )}


          {/* iframe section */}
          {active.iframe && (
            <iframe
              key={activeId}
              src={active.iframe}
              className="flex-1 w-full border-0"
              title={active.label}
            />
          )}

          {/* Section body */}
          {!active.iframe && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="max-w-3xl mx-auto px-8 py-8 space-y-4"
            >
              {active.content.map((block, i) => <Block key={i} block={block} />)}

              {/* Next section card */}
              {SECTIONS.findIndex(s => s.id === activeId) < SECTIONS.length - 1 && (() => {
                const next = SECTIONS[SECTIONS.findIndex(s => s.id === activeId) + 1];
                return (
                  <button
                    onClick={() => setActiveId(next.id)}
                    className="mt-8 w-full flex items-center justify-between p-4 rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <DynamicIcon name={next.icon} className="w-5 h-5 text-indigo-500" />
                      <div className="text-left">
                        <p className="text-xs text-indigo-400 mb-0.5">Next Section</p>
                        <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">{next.label}</p>
                      </div>
                    </div>
                    <DynamicIcon name="ArrowRight" className="w-4 h-4 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                  </button>
                );
              })()}
            </motion.div>
          </AnimatePresence>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserGuidePage;
